import './perks-avatar.component.scss';

import { h } from 'preact';

import { IconUserCircle } from '../../../common/icons/icon-user-circle';
import { useTranslation } from '../../../i18n';
import { avatarGradient } from '../../avatar-gradient';
import type { User } from '../../model/user.interface';
import { useDisplayIdentity } from '../../use-display-identity.hook';
import {
  AVATAR_BADGE_SIZE_PX,
  badgeKindForUser,
  PerksBadge,
} from '../perks-badge/perks-badge.component';

interface Props {
  /**
   * may be missing or have no id: a shadow-banned commenter reaches other fans with no user, and a
   * logged-out visitor's user is anonymous, with a null id. Either falls back to the plain
   * silhouette -- a throw here aborts the whole render and takes the surface around it with it.
   */
  user: User | null | undefined;
  size: number;
  /**
   * px size for the verified badge — the large profile avatars pass PROFILE_BADGE_SIZE_PX, the
   * compact ones beside a comment input and on the top-comment strip pass COMPACT_AVATAR_BADGE_SIZE_PX
   */
  badgeSize?: number;
  onImageLoad?: () => void;
}

export const PerksAvatar = ({
  user,
  size,
  badgeSize = AVATAR_BADGE_SIZE_PX,
  onImageLoad,
}: Props) => {
  const { t } = useTranslation();
  const { name, imageUrl } = useDisplayIdentity(user);
  return (
    <div
      class="single-avatar"
      style={{
        width: `${size}px`,
        height: `${size}px`,
      }}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          style={{
            width: `${size}px`,
            height: `${size}px`,
          }}
          alt={t('perks.avatarAlt', { name })}
          onLoad={onImageLoad}
        />
      ) : user?.id ? (
        <span
          class="single-avatar-gradient"
          role="img"
          aria-label={t('perks.avatarAlt', { name })}
          // sized by the stylesheet to fill .single-avatar, not inline: a surface that resizes the
          // avatar (the comment header on mobile) overrides the wrapper, and an inline size here
          // would leave the gradient at full height inside the narrower box and squash it to an oval
          style={{ background: avatarGradient(user.id) }}
        >
          <IconUserCircle width={size} height={size} fill="#fff" />
        </span>
      ) : (
        // no user id to derive a gradient from: the gray silhouette, as before SNGL-7360
        <IconUserCircle width={size} height={size} />
      )}
      {/* the verified check lives on the avatar, so every surface a user appears on carries it
          without each one having to place it next to its own name */}
      <PerksBadge kind={badgeKindForUser(user)} size={badgeSize} />
    </div>
  );
};
